/**
 * Interactive UI guides — named, code-reviewed walkthroughs that Scout can
 * offer ("Do you want me to show you?") and the tour engine plays.
 *
 * Shared between server and UI on purpose: the server (Scout's
 * offer_ui_guide tool) validates guide ids + role gates so the model can
 * only ever offer walkthroughs that exist and that the user may perform;
 * the UI resolves the same id to steps and plays them. Because guides are
 * code, they change in the same PR as the screens they walk through —
 * a ui test asserts every anchor referenced here exists in the source.
 *
 * Step routes may contain ":tenantId", substituted by the tour engine.
 * Steps without an anchor render as a centered card (no spotlight).
 */
export const GUIDE_SECTIONS = [
    { id: "chat", label: "Chat" },
    { id: "agents", label: "Agents" },
    { id: "workflows", label: "Workflows" },
    { id: "help", label: "Help" },
];
export const GUIDE_ROLE_LEVEL = {
    standard_user: 0,
    agent_admin: 1,
    tenant_admin: 2,
};
export const UI_GUIDES = [
    // Platform basics was one 17-step tour; it is now three short ones (Kenneth,
    // 2026-10-01). The id stays "platform-basics" for the chat guide, because
    // the first-login welcome and the legacy start-tour event open it by id.
    {
        id: "platform-basics",
        section: "chat",
        title: "Chat with your agent",
        requiredRole: "standard_user",
        summary: "The first-login tour: pick an agent, send it a message, see the reply, and meet Scout.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='agent-picker']",
                title: "Your agents",
                body: "Every agent you can talk to, grouped by what matters right now: agents that need you first, agents working on something second, the rest tucked under “more agents”. Click a name to switch to it.",
            },
            {
                anchor: "[data-tour='chats']",
                title: "Recent chats",
                body: "Your conversations with the selected agent. Each chat keeps its own context, so use New chat when you switch topics.",
            },
            // Hands-on chat: a fresh chat, the user sends a message of their own,
            // the guide waits for the reply and then points at it. An error reply
            // counts too (common locally, where agents can't be reached).
            {
                anchor: "[data-tour='composer']",
                event: "proclaw:guide-new-chat",
                noMask: true,
                advanceOnEvent: "proclaw:chat-message-sent",
                requireEvent: true,
                waitHint: "Send a message to carry on",
                suggestions: [
                    "What can you help me with?",
                    "Give me three ideas to save time this week",
                    "Explain what you do in one short paragraph",
                ],
                title: "Talk to your agent",
                body: "Say hello. Type anything and press Enter, or pick one of these to drop it in the box. Later, type @ to bring in another agent or a workspace file, and / to run a skill.",
            },
            {
                anchor: "[data-tour='chat-messages']",
                noMask: true,
                advanceOnEvent: "proclaw:chat-reply-done",
                catchUpEvent: true,
                requireEvent: true,
                waitHint: "Waiting for the reply…",
                title: "Your agent is replying",
                body: "Watch the answer come in. It can take a little while when the agent uses tools along the way.",
            },
            {
                anchor: "[data-tour='chat-last-reply']",
                title: "Your agent's reply",
                body: "Here's the reply. Steps the agent took show above the answer, and if something went wrong you'll see the error here with a Retry button.",
            },
            {
                anchor: "[data-tour='scout']",
                interactive: true,
                title: "Meet Scout",
                body: "Stuck, or something isn't behaving? Scout explains how the platform works, checks agent and workflow health, and can raise a support request. More short guides live there too. Click it to open Scout.",
            },
        ],
    },
    {
        id: "channels",
        section: "chat",
        title: "Channels",
        requiredRole: "standard_user",
        summary: "What channels are for, and making one when you manage the agent.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='channels']",
                title: "Channels",
                body: "Channels are spaces for one topic or team. A shared channel shows everyone's chats in it; a private one shows each person only their own. Chats outside a channel live under the agent's General area.",
            },
            // Hands-on channel steps. The + only shows to people who can manage this
            // agent (the server refuses everyone else), so for them these skip.
            {
                anchor: "[data-tour='channel-add']",
                interactive: true,
                optional: true,
                title: "Make a channel",
                body: "Let's make one. Click + to start a new channel.",
            },
            {
                anchor: "[data-tour='channel-form']",
                noMask: true,
                optional: true,
                advanceOnEvent: "proclaw:channel-created",
                requireEvent: true,
                backIfClosed: true,
                waitHint: "Create the channel to carry on",
                title: "Name your channel",
                body: "Give it a name, for example \"planning\". Choose Private or Shared, then click Create channel.",
            },
            {
                // Shown after the channel is made: the app opens it.
                anchor: "[data-tour='channel-active']",
                onlyAfterEvent: "proclaw:channel-created",
                title: "Your new channel",
                body: "There it is, and you're in it now. New chats you start here live in this channel.",
            },
        ],
    },
    {
        id: "files-tools-skills",
        section: "chat",
        title: "Files, tools and skills",
        requiredRole: "standard_user",
        summary: "Attach files, choose the agent's tools, run a skill, and open the workspace panel.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='attach']",
                title: "Share files",
                body: "The + button attaches files: documents and images (up to 5 per message) your agent can read and work with.",
            },
            {
                anchor: "[data-tour='composer-tools']",
                interactive: true,
                title: "Choose tools",
                body: "The tools button controls which tools this agent can use. Click it to open the list.",
            },
            {
                anchor: "[data-tour='tools-popup']",
                noMask: true,
                title: "Tools available here",
                body: "This shows the tools available to the agent. Switch a whole set on or off, or expand one to pick single tools.",
            },
            {
                anchor: "[data-tour='composer-skill']",
                interactive: true,
                title: "Run a skill",
                body: "The / button lists skill shortcuts you can run in a message. Click it to open the list.",
            },
            {
                anchor: "[data-tour='skill-popup']",
                noMask: true,
                title: "Skill shortcuts",
                body: "These are the skill shortcuts this agent has. Pick one to drop it into your message.",
            },
            {
                anchor: "[data-tour='workspace-trigger']",
                interactive: true,
                title: "Open the workspace",
                body: "This opens your agent's workspace files and outputs in a panel on the right. Click it to open the panel.",
            },
            {
                anchor: "[data-tour='workspace-panel']",
                noMask: true,
                title: "Files and outputs",
                body: "Your agent's files, the things it makes, and its browser view open here on the right.",
            },
        ],
    },
    {
        id: "workflow-results",
        section: "workflows",
        title: "See what your workflows did",
        requiredRole: "standard_user",
        summary: "Open a workflow's insights, pick a run, and read what each step did.",
        requiresWorkflow: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=workflows",
                anchor: "[data-tour='workflow-table']",
                title: "Your workflows",
                body: "Every workflow in your company: how often it ran, how often it worked, and when it last ran.",
            },
            {
                anchor: "[data-tour='workflow-row-menu']",
                interactive: true,
                title: "Open a workflow's menu",
                body: "Click ⋯ at the end of a row.",
            },
            {
                anchor: "[data-tour='workflow-view-insights']",
                interactive: true,
                title: "View its insights",
                body: "Choose View insights.",
            },
            {
                anchor: "[data-tour='workflow-runs']",
                interactive: true,
                title: "Pick a run",
                body: "Every run of this workflow, newest first. Click one to see what happened.",
            },
            {
                anchor: "[data-tour='workflow-run-panel']",
                title: "What the run did",
                body: "Each step in order: which agent did it, how long it took, and where it stopped if it failed.",
            },
        ],
    },
    {
        id: "raise-support-ticket",
        section: "help",
        title: "Raise a support request",
        requiredRole: "standard_user",
        summary: "Get a problem to the Aztech team and follow its progress.",
        steps: [
            {
                anchor: "[data-tour='scout']",
                interactive: true,
                title: "Start with Scout",
                body: "Scout is the help button in the corner. Describe a problem to it first: it can often check your agents and workflows and fix it on the spot. Click it now.",
            },
            {
                anchor: "[data-tour='scout-support']",
                interactive: true,
                title: "Your support requests",
                body: "The life-buoy icon at the top of Scout lists your requests to the Aztech team. Click it.",
            },
            {
                anchor: "[data-tour='support-new']",
                interactive: true,
                title: "Start a request",
                body: "Click New to open the request form.",
            },
            {
                // An explanation, not a hands-on step: a walkthrough must never send a
                // real request to the support team.
                anchor: "[data-tour='support-form']",
                noMask: true,
                title: "Describe the problem",
                body: "Give it a subject, say what went wrong, and choose how you'd like to hear back. Nothing is sent until you click Submit request. You'll get a notification whenever the team replies or the status changes.",
            },
        ],
    },
    {
        id: "create-first-agent",
        section: "agents",
        title: "Create your first agent",
        requiredRole: "tenant_admin",
        summary: "Create and deploy your first AI agent from a template.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='profile-menu']",
                interactive: true,
                title: "Open your profile menu",
                body: "Click your profile at the bottom of the left sidebar. Everything you manage for the workspace lives behind here.",
            },
            {
                anchor: "[data-tour='menu-tenant-settings']",
                interactive: true,
                title: "Open Settings",
                body: "Choose Settings to open the workspace management dialog.",
            },
            {
                anchor: "[data-tour='settings-nav-agents']",
                interactive: true,
                title: "Go to Agents",
                body: "Open the Agents section. This lists your agents and the New Agent button.",
            },
            {
                anchor: "[data-tour='agent-new']",
                interactive: true,
                title: "Start a new agent",
                body: "Click New Agent to see the ways you can add one.",
            },
            {
                anchor: "[data-tour='agent-deploy-instance']",
                interactive: true,
                title: "Deploy a new instance",
                body: "Choose Deploy New Instance. The setup wizard opens right here in the dialog, so you never leave this screen.",
            },
            {
                anchor: "[data-tour='agent-templates']",
                interactive: true,
                title: "Pick a template",
                body: "Every agent starts from a template that sets sensible defaults for its role, runtime and tools. Click the one closest to the job.",
            },
            {
                anchor: "[data-tour='wizard-continue']",
                interactive: true,
                title: "Continue",
                body: "Click Continue to move on to naming and configuring your agent.",
            },
            {
                title: "You take it from here",
                body: "Step through identity, model and infrastructure, then Deploy when you're happy.",
            },
        ],
    },
    {
        id: "change-agent-model",
        section: "agents",
        title: "Change an agent's AI model",
        requiredRole: "agent_admin",
        summary: "Switch an agent's model, set a backup, then sync it.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='agent-settings']",
                interactive: true,
                title: "Open agent settings",
                body: "With an agent selected, click the settings icon at the top right, next to the notifications bell, to open its settings.",
            },
            {
                anchor: "[data-tour='model-config']",
                noMask: true,
                advanceOnEvent: "proclaw:agent-model-synced",
                requireEvent: true,
                waitHint: "Click Update & Sync to carry on",
                title: "Change the model",
                body: "This is the Model box. Pick a new primary model here, and optionally a backup model used if the primary is unavailable. Then click Update & Sync to apply it to the agent.",
            },
            {
                anchor: "[data-tour='model-config']",
                title: "That's it",
                body: "The agent picks up the new model straight away. If the sync didn't go through, the Model box says why, and you can try Update & Sync again.",
            },
        ],
    },
    {
        id: "give-agent-tool",
        section: "agents",
        title: "Give an agent a tool",
        requiredRole: "agent_admin",
        summary: "Assign an MCP server from your workspace to an agent, then sync it to the agent.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='agent-settings']",
                interactive: true,
                title: "Open agent settings",
                body: "With an agent selected, click the settings icon at the top right, next to the notifications bell.",
            },
            {
                anchor: "[data-tour='agent-mcp-card']",
                title: "The agent's tools",
                body: "These are the MCP servers this agent can use, and the tools in each. Open one to switch single tools on or off.",
            },
            {
                // Only there when the workspace has a server this agent doesn't have.
                anchor: "[data-tour='agent-mcp-assign']",
                noMask: true,
                optional: true,
                advanceOnEvent: "proclaw:agent-mcp-assigned",
                requireEvent: true,
                waitHint: "Assign a server to carry on",
                title: "Assign a server",
                body: "Pick a server from your workspace's list and click Assign.",
            },
            {
                anchor: "[data-tour='agent-mcp-sync']",
                noMask: true,
                skipIfDisabled: true,
                advanceOnEvent: "proclaw:agent-mcp-synced",
                requireEvent: true,
                waitHint: "Click Sync to Agent to carry on",
                title: "Sync it to the agent",
                body: "Click Sync to Agent so the agent picks up the change.",
            },
            {
                anchor: "[data-tour='agent-mcp-card']",
                title: "That's it",
                body: "If the sync didn't go through, the message here says why. A server missing from the list? Add it to your workspace first: the \"Add an MCP server\" guide shows how.",
            },
        ],
    },
    {
        // Open to agent admins: the server lets them add a workspace MCP server
        // (requireCompanyMutationAccess) and the settings popup shows them MCP
        // Servers. It ends at the workspace list; "Give an agent a tool" carries on.
        id: "add-mcp-server",
        section: "agents",
        title: "Add an MCP server to your workspace",
        requiredRole: "agent_admin",
        summary: "Register an MCP server for the workspace, so it can then be given to agents.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='profile-menu']",
                interactive: true,
                title: "Open your profile menu",
                body: "Click your profile at the bottom of the left sidebar to reach the workspace settings.",
            },
            {
                anchor: "[data-tour='menu-tenant-settings']",
                interactive: true,
                title: "Open Settings",
                body: "Choose Settings to open the workspace management dialog.",
            },
            {
                anchor: "[data-tour='tenant-nav-mcp']",
                interactive: true,
                title: "Go to MCP Servers",
                body: "In the menu on the left, under Agent tools, choose MCP Servers. It lists every MCP tool your workspace can use: the ones your provider published and your own.",
            },
            {
                anchor: "[data-tour='mcp-create']",
                interactive: true,
                title: "Create an MCP server",
                body: "Click Create MCP to register your own server. (Or click Enable on a published server to switch it on without creating one.)",
            },
            {
                anchor: "[data-tour='mcp-editor']",
                noMask: true,
                advanceOnEvent: "proclaw:mcp-saved",
                requireEvent: true,
                backIfClosed: true,
                waitHint: "Save the server to carry on",
                title: "Fill it in and save",
                body: "Enter the name, transport, URL and authentication (any secrets are kept in your company's private vault), then click Create MCP Server to save it.",
            },
        ],
    },
    {
        id: "retry-failed-run",
        section: "workflows",
        title: "Check a failed run and retry it",
        requiredRole: "agent_admin",
        summary: "Find a failing workflow, see where its run stopped, and run it again.",
        requiresWorkflow: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=workflows",
                anchor: "[data-tour='workflow-table']",
                title: "Find a failing workflow",
                body: "The Success column shows how often each workflow worked. Anything under 100% had runs that failed. If every workflow shows 100%, there's nothing to retry, and the guide ends here.",
            },
            {
                // The first workflow with failed runs. None at all: the guide ends.
                anchor: "[data-tour='workflow-row-menu-failing']",
                interactive: true,
                optional: true,
                stopIfMissing: true,
                title: "Open its menu",
                body: "This workflow has failed runs. Click ⋯ at the end of its row.",
            },
            {
                anchor: "[data-tour='workflow-view-insights']",
                interactive: true,
                title: "View its insights",
                body: "Choose View insights.",
            },
            {
                anchor: "[data-tour='workflow-failed-run']",
                interactive: true,
                title: "Open a failed run",
                body: "Failed runs are marked in red. Click one.",
            },
            {
                anchor: "[data-tour='workflow-run-panel']",
                title: "Where it stopped",
                body: "Each step in order. For a failed run, the step that failed is marked, with the reason underneath.",
            },
            {
                anchor: "[data-tour='workflow-retry']",
                interactive: true,
                optional: true,
                title: "Run it again",
                body: "Fixed the cause? Click Retry run to run it again from the start.",
            },
        ],
    },
    {
        id: "create-workflow",
        section: "workflows",
        title: "Build a workflow",
        requiredRole: "tenant_admin",
        summary: "Open the Workflows view, start one, and build it on the canvas.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=workflows",
                anchor: "[data-tour='workflow-create']",
                interactive: true,
                title: "Start a workflow",
                body: "This is your Workflows list. Click New Workflow to begin one.",
            },
            {
                // The name comes first, on its own step: Create stays disabled until
                // the field has text, so highlighting Create first pointed at a button
                // that could not be clicked yet.
                // Next here, by Kenneth's choice: moving on by itself after a pause
                // in typing felt rushed.
                anchor: "[data-tour='workflow-name']",
                noMask: true,
                title: "Name your workflow",
                body: "Type a name for your workflow here, for example \"Lead triage\", then click Next.",
            },
            {
                anchor: "[data-tour='workflow-create-confirm']",
                interactive: true,
                noMask: true,
                // Enter in the name box creates it straight away; don't wait long.
                optional: true,
                title: "Create it",
                body: "Now click Create. Your new workflow opens on the visual canvas.",
            },
            {
                // Moves on when a node is dropped, not when a drag starts: a drag let
                // go short of the canvas used to race through Configure and Save
                // (nothing to configure, nothing to save) to the last step.
                anchor: "[data-tour='node-palette']",
                noMask: true,
                advanceOnEvent: "proclaw:workflow-node-added",
                requireEvent: true,
                waitHint: "Drop a node on the canvas to carry on",
                title: "Drag a node onto the canvas",
                body: "Drag a node from this palette onto the canvas: a trigger to start, then agent steps, conditions and actions.",
            },
            {
                anchor: "[data-tour='config-panel']",
                noMask: true,
                title: "Configure the node",
                body: "The node you dropped is selected, and its settings open here on the right. This is where you set what it does and connect it onward. You'll save it in the next step.",
            },
            {
                anchor: "[data-tour='workflow-save']",
                noMask: true,
                advanceOnEvent: "proclaw:workflow-saved",
                requireEvent: true,
                skipIfDisabled: true,
                waitHint: "Click Save to carry on",
                title: "Save your workflow",
                body: "When the node is set up, click Save to keep your workflow.",
            },
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='composer']",
                title: "Or build it from chat",
                body: "You don't have to build it by hand. You can also describe the workflow to an agent here in chat, and it drafts one for you to open and refine on the canvas.",
            },
        ],
    },
];
export function getUiGuide(id) {
    return UI_GUIDES.find((g) => g.id === id);
}
//# sourceMappingURL=ui-guides.js.map